import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../api';

/**
 * WelcomeModal — the first thing a freshly-signed-up user sees on their
 * first landing at /home. A brief, friendly primer on what Clanster is,
 * with a smooth handoff into the OnboardingTour.
 *
 * Copy is fully i18n'd via the `welcome.*` bundle in
 * `/app/frontend/src/i18n/locales/{en,es,pt,fr}.json`. Bold segments are
 * rendered via the <Trans> component with a <0>…</0> placeholder that maps
 * to a `<b>` tag, so the translator can move the emphasis anywhere in the
 * sentence without touching JSX.
 *
 * Auto-boots when:
 *   • authenticated user is loaded, and
 *   • user is on /home (matches OnboardingTour gate), and
 *   • `user.welcome_shown_at` is unset (i.e. first-run), and
 *   • user is not admin and not suspended.
 *
 * Both CTAs — `Start Tour` and `Dismiss` — POST to `/auth/welcome-shown`
 * which stamps `welcome_shown_at`. After refreshMe() the OnboardingTour
 * auto-boots on its own since `tour_completed_at` is still unset.
 */
export default function WelcomeModal() {
  const { user, refreshMe } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || user === false) return;
    if (user.welcome_shown_at) return;
    if (user.role === 'admin') return;
    if (user.suspended) return;
    if (location.pathname !== '/home') return;
    const timer = setTimeout(() => setOpen(true), 350);
    return () => clearTimeout(timer);
  }, [user, location.pathname]);

  const markShown = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post('/auth/welcome-shown');
      await refreshMe();
    } catch (e) {
      // Non-fatal — even if the POST fails we still close so the user
      // isn't blocked. Modal will re-appear next reload only if the DB
      // write also failed.
    } finally {
      setOpen(false);
      setBusy(false);
    }
  };

  if (!open || !user) return null;

  const listKeys = ['list1', 'list2', 'list3', 'list4'];
  const listEmojis = ['🎨', '🚀', '💬', '🎁'];

  return (
    <div className="wm-overlay" role="dialog" aria-modal="true" aria-labelledby="wm-title" data-testid="welcome-modal">
      <div className="wm-card" data-testid="welcome-modal-card">
        <h2 id="wm-title" className="wm-title" data-testid="welcome-modal-title">
          {t('welcome.title')} <span aria-hidden="true">👋</span>
        </h2>
        <p className="wm-sub" data-testid="welcome-modal-sub">
          {t('welcome.sub')}
        </p>
        <ul className="wm-list" data-testid="welcome-modal-list">
          {listKeys.map((key, i) => (
            <li key={key}>
              <span className="wm-emoji" aria-hidden="true">{listEmojis[i]}</span>
              <span>
                <Trans i18nKey={`welcome.${key}`} components={[<b key="b" />]} />
              </span>
            </li>
          ))}
        </ul>
        <p className="wm-tip" data-testid="welcome-modal-tip">
          <Trans i18nKey="welcome.tip" components={[<b key="b" />]} />
        </p>
        <p className="wm-cta-lead" data-testid="welcome-modal-cta-lead">
          {t('welcome.ctaLead')}
        </p>
        <div className="wm-actions">
          <button
            type="button"
            className="wm-btn wm-btn-secondary"
            onClick={markShown}
            disabled={busy}
            data-testid="welcome-modal-dismiss-btn"
          >
            {t('welcome.dismiss')}
          </button>
          <button
            type="button"
            className="wm-btn wm-btn-primary"
            onClick={markShown}
            disabled={busy}
            data-testid="welcome-modal-start-tour-btn"
          >
            {t('welcome.startTour')}
          </button>
        </div>
      </div>
    </div>
  );
}
